import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Alert, Badge, Button, Checkbox, DataTable, DefinitionList, Dialog, ErrorSummary, Inline, RadioGroup, Select, SplitLayout, Stack, t, Text, TextField,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { RefusalPage } from '../components/Refusal';
import { PendingButton } from '../components/PendingButton';
import { RenameDialog } from '../components/RenameDialog';
import { FindingsTable } from '../components/DeployAnalysisDrawer';
import { EnvironmentVariables } from '../components/EnvironmentVariables';
import { useEnvironmentName } from '../lib/environments';
import { ACCESS_USER, AccessFields, drawAccessPassword } from '../components/install/AccessFields';
import { EMPTY_DRAFT, fieldIdOf, parseDraft, partOf, ProjectDraft, ProjectStep, resolveDraft, technicalDomain } from '../components/install/ProjectStep';
import { ApplicationsStep, BatchRow, keptApplications } from '../components/install/ApplicationsStep';
import { BatchVariablesDrawer } from '../components/install/BatchVariablesDrawer';
import { InstallationFollowUp } from '../components/install/InstallationFollowUp';

type Installation = maintenance.components['schemas']['Installation'];
type DraftError = { step: string; field: string; message: string };

/** D96, D103: « Projet », then the repository and its environment, its applications, their addresses and the review (5 steps, the DSM's limit). */
const STEPS = ['project', 'repository', 'applications', 'address', 'review'] as const;
const REPOSITORY = 1;
const APPLICATIONS = 2;
const ADDRESS = 3;
const REVIEW = 4;
const ACME = ['production', 'staging', 'http'] as const;
const DOMAIN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * SPEC-034 § 6 (lot 1.5): « Installer une application » from a reserve server, on the DSM SplitLayout with steps, as
 * the enrolment (D66). D103: a repository is installed whole — each of its deploy directories is an application,
 * installed one after the other. The choices live in the URL, so the strong authentication asked at the end brings
 * the person back to them.
 */
export function InstallProjectPage() {
  const { server = '' } = useParams();
  const navigate = useNavigate();
  const envName = useEnvironmentName();
  const [params, setParams] = useSearchParams();
  const step = Math.min(Number(params.get('step') ?? 0) || 0, STEPS.length - 1);
  const draft = parseDraft(params.get('draft'));
  const acme = (params.get('acme') ?? 'production') as (typeof ACME)[number];
  const started = params.get('installation');
  const batch = params.get('batch');
  // D100: the access password is shown only by the page that drew it and started the installation (never on a later
  // visit of this follow-up: the platform keeps the hash alone, a new random value would be a lie).
  const [startedHere, setStartedHere] = useState(false);
  // D100: out of production, the access is behind a password by default. The password is drawn here and lives in
  // this page only (never in the URL); after the strong authentication it is drawn again, and shown once started.
  const accessUser = params.get('accessUser') ?? 'kanbios';
  const [accessPassword, setAccessPassword] = useState(drawAccessPassword);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [stepUp, setStepUp] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // « Continuer » clicked while the step is being checked: kept, and applied once the check is back.
  const [advancing, setAdvancing] = useState(false);
  const nextRef = useRef<() => void>(() => undefined);
  // D105: the application whose variables are open, and the secrets typed in this page (for the export only).
  const [variablesOf, setVariablesOf] = useState<string | null>(null);
  const [typedSecrets, setTypedSecrets] = useState<Record<string, Record<string, string>>>({});

  const list = useQuery({
    queryKey: ['installations', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/installations', { params: { path: { server } } })),
    retry: false,
  });
  // § 6.1 step 1: the clients, projects and environments to choose from, or to create.
  const options = useQuery({
    queryKey: ['declare-options', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/applications/options', { params: { path: { server } } })),
    retry: false,
  });
  const chosen = resolveDraft(draft, options.data);
  const setDraft = (d: ProjectDraft) => set({ draft: JSON.stringify(d) });
  // SPEC-034 § 6.1: the branch follows the environment (develop for acceptance, main beyond); it can be changed.
  const ref = params.get('ref') ?? (chosen.environmentType && chosen.environmentType !== 'acceptance' && chosen.environmentType !== 'development' ? 'main' : 'develop');
  const protect = chosen.environmentType !== 'production' && params.get('access') !== 'off';
  const access = protect ? { user: accessUser, password: accessPassword } : null;
  const base = { server, repository: chosen.repository ?? '', ref, acme, draft: chosen.draft };
  // D103: the repository's deploy directories, each proposed as an application of the project.
  const directories = useQuery({
    queryKey: ['installation-batch-directories', server, chosen.draft, chosen.repository, ref],
    queryFn: async () => unwrap(await api.maintenance.POST('/installations/batch/preview', { body: base })),
    enabled: chosen.complete && step >= APPLICATIONS,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const kept = keptApplications(draft, directories.data?.directories ?? []);
  // The domains count at the review only (their refusals and DNS): typing one does not check the repository again.
  const checked = step === REVIEW ? kept : kept.map(k => ({ ...k, domain: '' }));
  // The kept ones, checked as they will be installed: the catalogue's rules, their contract, the isolation rules.
  const preview = useQuery({
    queryKey: ['installation-batch-preview', server, chosen.draft, chosen.repository, ref, acme, checked],
    queryFn: async () => unwrap(await api.maintenance.POST('/installations/batch/preview', { body: { ...base, applications: checked } })),
    enabled: chosen.complete && step >= APPLICATIONS && kept.length > 0,
    // While a change is checked, the last answer stays on screen (no blank between two answers).
    placeholderData: keepPreviousData,
    retry: false,
  });
  const rows: BatchRow[] = preview.data?.applications ?? [];
  const exposedRows = rows.filter(r => r.analysis.exposed.length > 0);
  // SPEC-034 § 10 (D98): outside production, a technical domain of the platform is suggested (its record is created for it).
  const suggestedFor = (name: string) => (chosen.environmentType === 'production' ? '' : technicalDomain(
    { client: chosen.clientName, project: chosen.projectName, application: exposedRows.length > 1 ? name : null, environment: chosen.environmentName },
    list.data?.technicalZone ?? null,
  ));
  useEffect(() => {
    if (step !== ADDRESS || !exposedRows.length) return;
    const missing = exposedRows.filter(r => draft.domains[r.deployPath] === undefined && suggestedFor(r.name));
    if (missing.length) setDraft({ ...draft, domains: { ...draft.domains, ...Object.fromEntries(missing.map(r => [r.deployPath, suggestedFor(r.name)])) } });
  }, [step, exposedRows.length]);
  // The fields the backend refused (same rules as « Déclarer une application »).
  const draftErrors: DraftError[] = preview.data?.errors
    ?? (preview.error instanceof ApiFailure && preview.error.body.code === 'VALIDATION_FAILED' ? ((preview.error.body.details as { errors?: DraftError[] } | undefined)?.errors ?? []) : []);
  const followed = useQuery({
    queryKey: ['installation', started],
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/{id}', { params: { path: { id: started! } } })),
    enabled: Boolean(started),
    refetchInterval: q => (q.state.data && ['ready', 'in_service', 'failed', 'abandoned'].includes(q.state.data.state) && !q.state.data.waitingFor ? false : 3_000),
  });
  const followedBatch = useQuery({
    queryKey: ['installation-batch', batch],
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/batch/{batch}', { params: { path: { batch: batch! } } })),
    enabled: Boolean(batch),
    refetchInterval: q => (q.state.data && q.state.data.installations.every(i => ['in_service', 'abandoned'].includes(i.state)) ? false : 3_000),
  });
  // An installation of a batch is followed with its batch (a link from the server page names the installation only).
  useEffect(() => {
    if (followed.data?.batch && !batch && params.get('view') !== 'variables') set({ batch: followed.data.batch, installation: null });
  }, [followed.data?.batch]);
  // § 6.1, D22: once in service, the server can take a production name (<client>-<project>-<role>-<env>-<NN>).
  const rename = useQuery({
    queryKey: ['server-rename', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/rename', { params: { path: { server } } })),
    enabled: Boolean((followed.data && ['deployed', 'in_service'].includes(followed.data.state)) || followedBatch.data?.installations.every(x => x.state === 'in_service')),
    retry: false,
  });
  useEffect(() => {
    if (!advancing || preview.isFetching || directories.isFetching) return;
    setAdvancing(false);
    nextRef.current();
  }, [advancing, preview.isFetching, directories.isFetching]);
  // Back from the strong authentication: the installation is started at once.
  useEffect(() => {
    if (params.get('confirm') === '1' && chosen.complete && kept.length && !started && !batch && !sending) {
      set({ confirm: null });
      void start();
    }
  }, [chosen.complete, kept.length]);

  const leave = () => navigate(`/servers/${encodeURIComponent(server)}`);
  // DSM SplitLayout: « Retour » leaves without losing work — ask first when something was chosen or typed.
  const touched = !started && !batch && (JSON.stringify(draft) !== JSON.stringify(EMPTY_DRAFT) || step > 0);
  const quit = () => (touched ? setLeaving(true) : leave());
  if (list.error) return <RefusalPage error={list.error} />;
  if (options.error) return <RefusalPage error={options.error} />;

  const domainOf = (r: BatchRow) => draft.domains[r.deployPath] ?? '';
  const errorsOf = (s: number) => {
    const errors: { message: string; fieldId: string }[] = [];
    if (s === 0 && !chosen.projectComplete) errors.push({ message: t('install.errors.project'), fieldId: draft.client ? 'install-project' : 'install-client' });
    if (s === REPOSITORY && !chosen.repositoryComplete) errors.push({ message: t('install.errors.repository'), fieldId: chosen.repository ? 'install-environment' : 'install-repository' });
    if (s === APPLICATIONS && directories.data && !directories.data.directories.length) errors.push({ message: t('install.errors.noDirectory', { ref }), fieldId: 'ref' });
    if (s === APPLICATIONS && directories.data?.directories.length && !kept.length) errors.push({ message: t('install.errors.noApplication'), fieldId: 'install-applications' });
    if (s === APPLICATIONS) kept.forEach((k, i) => { if (!k.role) errors.push({ message: t('install.errors.roleOf', { name: k.name || k.deployPath }), fieldId: `app-${i}-role` }); });
    if (s === APPLICATIONS) rows.forEach(r => { if (r.variables.missing) errors.push({ message: t('install.errors.variablesOf', { name: r.name, n: r.variables.missing }), fieldId: `app-${kept.findIndex(k => k.deployPath === r.deployPath)}-variables` }); });
    if (s === APPLICATIONS) for (const r of rows) if (r.analysis.verdict === 'to_fix') errors.push({ message: t('install.errors.contractOf', { name: r.name }), fieldId: 'ref' });
    // The catalogue's rules on the whole draft; each error goes to its step and its field.
    const stepOf = (field: string) => ({ project: 0, repository: REPOSITORY, applications: APPLICATIONS })[partOf(field)];
    for (const e of draftErrors) if (stepOf(e.field) === s) errors.push({ message: e.message, fieldId: fieldIdOf(e.field, draft) });
    if (s === ADDRESS) for (const r of exposedRows) if (!DOMAIN.test(domainOf(r))) errors.push({ message: t('install.errors.domainOf', { name: r.name }), fieldId: `domain-${r.deployPath}` });
    if (s === ADDRESS && acme === 'http' && chosen.environmentType === 'production') errors.push({ message: t('install.errors.httpProduction'), fieldId: 'acme' });
    if (s === ADDRESS && exposedRows.length && protect && !ACCESS_USER.test(accessUser)) errors.push({ message: t('access.errors.user'), fieldId: 'access-user' });
    return errors;
  };
  const checking = (step === REPOSITORY || step === APPLICATIONS) && (directories.isFetching || preview.isFetching);
  const next = () => {
    if (checking) { setAdvancing(true); return; }
    // An error on an earlier step found by the check of the whole draft: back to that step, where its field is.
    const earlier = [0, REPOSITORY].find(s => s < step && errorsOf(s).length);
    if (earlier !== undefined) { set({ step: String(earlier) }); return setTried(true); }
    if (errorsOf(step).length) return setTried(true);
    setTried(false);
    set({ step: String(step + 1) });
  };
  nextRef.current = next;
  const refusals = rows.flatMap(r => r.refusals.filter(f => step === REVIEW || f.code !== 'domain').map(f => ({ ...f, application: r.name })));

  async function start() {
    setSending(true);
    setFailure(null);
    try {
      // Each domain as typed: the server leaves out those of internal applications (they expose nothing).
      const body = { ...base, applications: kept, access: exposedRows.length ? access : null };
      const created = unwrap(await api.maintenance.POST('/installations/batch', { body }));
      setStartedHere(true);
      set({ batch: created.batch });
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') setStepUp(true);
      else if (e instanceof ApiFailure) setFailure(e);
    } finally {
      setSending(false);
    }
  }
  const act = async (id: string, action: 'resume' | 'abandon') => {
    setFailure(null);
    try {
      unwrap(await api.maintenance.POST('/installations/{id}/{action}', { params: { path: { id, action } } }));
      void followed.refetch();
      void followedBatch.refetch();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') setStepUp(true);
      else if (e instanceof ApiFailure) setFailure(e);
    }
  };

  const stepUpDialog = (
    <Dialog
      open={stepUp}
      onClose={() => setStepUp(false)}
      title={t('settings.enrolment.stepUp.title')}
      description={t('install.stepUp')}
      confirmLabel={t('settings.enrolment.stepUp.confirm')}
      onConfirm={() => {
        const back = new URLSearchParams(params);
        if (!started && !batch) back.set('confirm', '1');
        window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent(`/servers/${server}/install?${back}`)}`);
      }}
    />
  );
  // D100: the password drawn by this page, shown until it is left: the platform keeps its hash only.
  const passwordAlert = (domain: string) => (
    <Alert tone="warn" title={t('access.keepTitle', { user: accessUser })}>
      <Stack gap={12}>
        <Text variant="body-sm">{t('access.keepBody', { domain })}</Text>
        <AccessFields user={accessUser} password={accessPassword} onUser={() => undefined} onPassword={() => undefined} readOnly />
      </Stack>
    </Alert>
  );

  // D103, D106: after the start, one follow-up for the batch (one application or several), each application's steps in place.
  if (started || batch) {
    const i: Installation | undefined = followed.data;
    // SPEC-034 § 12.4: once an installation is being retired (or is), its follow-up is the deployment's page.
    const retiring = i && (['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'].includes(i.state)
      || (i.state === 'failed' && ['retire', 'unlink', 'purge', 'reset'].includes(i.failedStep ?? '')));
    if (started && !batch && i && retiring) {
      return (
        <SplitLayout
          title={t('install.title', { server })}
          onBack={leave}
          backLabel={t('install.quit')}
          heading={t('install.retired.heading', { deployment: i.deployment })}
          lead={t('install.retired.lead')}
          footerEnd={<Button trailingIcon="arrow_forward" onClick={() => navigate(`/environments/${encodeURIComponent(i.deployment)}`)}>{t('install.retired.toEnvironment')}</Button>}
          footerText=""
        >
          <Alert tone="info" title={t(`environment.state.${i.state}`)}>{t('install.retired.body')}</Alert>
        </SplitLayout>
      );
    }
    // § 6.2 step 5: the variables of one installation (its secrets in its GitHub environment), from its follow-up.
    if (started && i && params.get('view') === 'variables') {
      const back = () => set({ view: null, ...(batch ? { installation: null } : {}) });
      return (
        <>
          <SplitLayout
            title={t('install.title', { server })}
            onBack={back}
            backLabel={t('install.variables.back')}
            heading={t('install.variables.heading', { deployment: i.deployment })}
            lead={t('install.variables.lead')}
            footerStart={<Button variant="secondary" icon="arrow_back" onClick={back}>{t('install.variables.back')}</Button>}
            footerEnd={<Button trailingIcon="arrow_forward" onClick={back}>{t('install.variables.done')}</Button>}
            footerText={i.missingSecrets.length ? t('install.secrets.title', { n: i.missingSecrets.length }) : ''}
          >
            <EnvironmentVariables installation={i.id} production={i.environmentType === 'production'} canWrite canRedeploy={false}
              onStepUp={() => setStepUp(true)} importing={false} onImportClose={() => undefined} />
          </SplitLayout>
          {stepUpDialog}
        </>
      );
    }
    const items = batch ? followedBatch.data?.installations ?? [] : i ? [i] : [];
    const protectedOnes = items.filter(x => x.access.protected);
    return (
      <>
        <InstallationFollowUp
          server={server}
          items={items}
          loading={batch ? followedBatch.isLoading : followed.isLoading}
          failure={failure ? <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>
            : followedBatch.error instanceof ApiFailure ? <Alert tone="neg" title={followedBatch.error.body.message}>{followedBatch.error.body.hint}</Alert> : undefined}
          password={protectedOnes.length > 0 && access && startedHere ? passwordAlert(protectedOnes.map(x => x.domain).join(', ')) : null}
          extra={rename.data?.canRename && rename.data.suggestion && rename.data.suggestion !== server && !rename.data.pullRequest ? (
            <Alert tone="info" title={t('install.rename.title', { name: rename.data.suggestion })} action={t('install.rename.action')} onAction={() => setRenaming(true)}>
              {t('install.rename.body', { server })}
            </Alert>
          ) : undefined}
          onAct={(x, action) => void act(x.id, action)}
          onVariables={x => set({ installation: x.id, view: 'variables' })}
          onOpen={path => navigate(path)}
          onLeave={leave}
          onProject={project => navigate(`/projects/${encodeURIComponent(project)}`)}
        />
        {stepUpDialog}
        {renaming && rename.data && (
          <RenameDialog rename={rename.data} onClose={() => setRenaming(false)}
            onOpened={() => { setRenaming(false); void rename.refetch(); }}
            onStepUp={() => { setRenaming(false); setStepUp(true); }} />
        )}
      </>
    );
  }

  const summaryErrors = tried ? errorsOf(step) : [];
  const todo = (text: string) => <Text variant="body-sm" tone="mute">{text}</Text>;
  const withNew = (text: string, isNew: boolean) => (isNew ? <Inline gap={8} align="center"><Text variant="body-sm">{text}</Text><Badge tone="brand" size="sm">{t('install.summary.new')}</Badge></Inline> : text);
  const toFix = rows.filter(r => r.analysis.findings.some(f => f.severity !== 'info'));
  return (
    <>
      <SplitLayout
        title={t('install.title', { server })}
        subtitle={t('install.subtitle')}
        steps={STEPS.map(s => t(`install.steps.${s}`))}
        current={step}
        onStepSelect={i => { if (i < step) set({ step: String(i) }); }}
        onBack={quit}
        backLabel={t('install.quit')}
        heading={t(`install.${STEPS[step]}.heading`)}
        lead={t(`install.${STEPS[step]}.lead`)}
        // As the enrolment (D66): the form on 2/3, the summary as plain content on the surface pane.
        split="2-1"
        asideLabel={t('install.summary.eyebrow')}
        aside={
          <Stack gap={16}>
            <Text variant="section-title" as="h2">{t('install.summary.title', { server })}</Text>
            <DefinitionList
              stacked
              items={[
                { key: 'client', label: t('install.summary.labels.client'), value: chosen.clientTitle ? withNew(chosen.clientTitle, chosen.creates[0]) : todo(t('install.summary.clientTodo')) },
                { key: 'project', label: t('install.summary.labels.project'), value: chosen.projectTitle ? withNew(chosen.projectTitle, chosen.creates[1]) : todo(t('install.summary.projectTodo')) },
                { key: 'repository', label: t('install.summary.labels.repository'), value: chosen.repository ? <Text variant="code">{`${chosen.repository} @ ${ref}`}</Text> : todo(t('install.summary.repositoryTodo')) },
                { key: 'deployment', label: t('install.summary.labels.deployment'), value: chosen.environmentName ? withNew(envName(chosen.environmentName), chosen.creates[2]) : todo(t('install.summary.deploymentTodo')) },
                {
                  key: 'applications', label: t('install.summary.labels.applications'),
                  value: kept.length && directories.data
                    ? <Stack gap={4}>{kept.map(k => <Text key={k.deployPath} variant="body-sm">{`${k.name}${k.domain && exposedRows.some(r => r.deployPath === k.deployPath) ? ` · ${k.domain}` : ''}`}</Text>)}</Stack>
                    : todo(t('install.summary.applicationsTodo')),
                },
                ...(exposedRows.length ? [{ key: 'acme', label: t('install.summary.labels.acme'), value: t(`install.acme.${acme}`) }] : []),
                ...(chosen.environmentType && chosen.environmentType !== 'production' && exposedRows.length
                  ? [{ key: 'access', label: t('install.summary.labels.access'), value: protect ? t('access.protected', { user: accessUser }) : t('access.public') }]
                  : []),
              ]}
            />
            <Text variant="body-sm" tone="mute">{t('install.summary.footer')}</Text>
          </Stack>
        }
        footerStart={<Button variant="secondary" icon="arrow_back" disabled={step === 0} onClick={() => set({ step: String(step - 1) })}>{t('install.previous')}</Button>}
        footerEnd={step < STEPS.length - 1
          ? <Button trailingIcon="arrow_forward" onClick={next}>{t('install.continue')}</Button>
          : <PendingButton icon="rocket_launch" pending={sending} disabled={refusals.length > 0 || draftErrors.length > 0 || preview.isFetching || !preview.data} onClick={() => void start()}>{t('install.startBatch', { n: kept.length })}</PendingButton>}
      >
        <Stack gap={24}>
          {summaryErrors.length > 0 && <ErrorSummary title={t('install.errorsTitle')} errors={summaryErrors} />}
          {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>}

          {step <= REPOSITORY && options.data && (
            <ProjectStep part={step === 0 ? 'project' : 'repository'} draft={draft} onChange={setDraft} options={options.data}
              errorOf={field => (tried || draftErrors.length ? draftErrors.find(e => e.field === field)?.message : undefined)} />
          )}

          {step === APPLICATIONS && (
            <Stack gap={16}>
              <Select label={t('install.repository.branch')} id="ref" options={[...new Set([ref, ...(rows[0]?.analysis.branches ?? ['develop', 'main'])])]} value={ref} onChange={v => v && set({ ref: v })} />
              {(directories.error ?? preview.error) instanceof ApiFailure && !draftErrors.length && (
                <Alert tone="neg" title={((directories.error ?? preview.error) as ApiFailure).body.message}>{((directories.error ?? preview.error) as ApiFailure).body.hint}</Alert>
              )}
              {directories.isFetching && !directories.data && <Text tone="mute">{t('install.applications.reading')}</Text>}
              {directories.data && directories.data.directories.length === 0 && (
                <Alert tone="warn" title={t('install.applications.noneTitle', { ref })}>{t('install.applications.noneBody')}</Alert>
              )}
              {directories.data && directories.data.directories.length > 0 && (
                <>
                  <Text variant="body-sm">{t('install.repository.commit', { commit: directories.data.commit.slice(0, 7), repository: directories.data.repository })}</Text>
                  <ApplicationsStep draft={draft} onChange={setDraft} directories={directories.data.directories} rows={rows} onVariables={setVariablesOf}
                    errorOf={field => (tried || draftErrors.length ? draftErrors.find(e => e.field === field)?.message : undefined)} />
                </>
              )}
              {toFix.map(r => (
                <Stack key={r.deployPath} gap={8}>
                  <Text variant="section-title" as="h3">{t('install.applications.findingsOf', { name: r.name, verdict: t(`deployContract.verdict.${r.analysis.verdict}`, { n: r.analysis.findings.filter(f => f.severity === (r.analysis.verdict === 'to_fix' ? 'blocking' : 'warning')).length }) })}</Text>
                  <FindingsTable findings={r.analysis.findings.filter(f => f.severity !== 'info')} />
                </Stack>
              ))}
            </Stack>
          )}

          {step === ADDRESS && !exposedRows.length && (
            <Alert tone="info" title={t('install.address.noneTitle')}>{t('install.address.noneBody')}</Alert>
          )}
          {step === ADDRESS && exposedRows.length > 0 && (
            <Stack gap={16}>
              {rows.length > exposedRows.length && <Text variant="body-sm" tone="mute">{t('install.address.internalOnes', { names: rows.filter(r => !r.analysis.exposed.length).map(r => r.name).join(', ') })}</Text>}
              {exposedRows.map(r => (
                <TextField key={r.deployPath} id={`domain-${r.deployPath}`} label={t('install.address.domainOf', { name: r.name })} mono value={domainOf(r)}
                  hint={suggestedFor(r.name) ? t('install.address.domainSuggested', { zone: list.data?.technicalZone ?? '' }) : t('install.address.domainHint')}
                  onChange={e => setDraft({ ...draft, domains: { ...draft.domains, [r.deployPath]: e.target.value.trim().toLowerCase() } })}
                  error={tried && !DOMAIN.test(domainOf(r)) ? t('install.errors.domain') : undefined} />
              ))}
              <RadioGroup label={t('install.address.acme')} value={acme} onChange={v => set({ acme: v })}
                options={ACME.map(a => ({ value: a, label: t(`install.acme.${a}`), description: t(`install.acme.${a}Help`), disabled: a === 'http' && chosen.environmentType === 'production' }))} />
              {/* D100: never in production (the site is public). One password for the exposed applications of the batch. */}
              {chosen.environmentType !== 'production' && (
                <Stack gap={12}>
                  <Checkbox label={t('access.protect')} checked={protect} onChange={on => set({ access: on ? null : 'off' })} />
                  <Text variant="body-sm" tone="mute">{t(exposedRows.length > 1 ? 'access.protectHelpBatch' : 'access.protectHelp')}</Text>
                  {protect && (
                    <AccessFields user={accessUser} password={accessPassword} onUser={u => set({ accessUser: u === 'kanbios' ? null : u })} onPassword={setAccessPassword}
                      error={tried && errorsOf(ADDRESS).some(x => x.fieldId === 'access-user') ? t('access.errors.user') : undefined} />
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {step === REVIEW && (
            <Stack gap={16}>
              {preview.isFetching && <Text tone="mute">{t('install.review.checking')}</Text>}
              {(refusals.length > 0 || draftErrors.length > 0) && (
                <Alert tone="neg" title={t('install.review.refused')}>
                  <Stack gap={4}>
                    {refusals.map(r => <Text key={`${r.application}-${r.code}`} variant="body-sm">{`${r.application} : ${r.message}`}</Text>)}
                    {draftErrors.map(e => <Text key={e.field} variant="body-sm">{e.message}</Text>)}
                  </Stack>
                </Alert>
              )}
              {preview.data && !refusals.length && !draftErrors.length && <Alert tone="pos" title={t('install.review.ok')}>{t('install.review.okBatch', { server, n: rows.length })}</Alert>}
              <DataTable
                label={t('install.review.order')}
                rows={[...rows.filter(r => !r.analysis.exposed.length), ...exposedRows]}
                rowKey="deployPath"
                toolbar={false}
                rowHeight="auto"
                columns={[
                  { key: 'order', label: '#', width: 56, align: 'right', render: (r: BatchRow) => String([...rows.filter(x => !x.analysis.exposed.length), ...exposedRows].indexOf(r) + 1) },
                  { key: 'deployment', label: t('install.batch.columns.deployment'), mono: true, strong: true, width: 260, render: (r: BatchRow) => (r.isNew ? withNew(r.deployment, true) : r.deployment) },
                  { key: 'domain', label: t('install.batch.columns.domain'), width: 260, render: (r: BatchRow) => (r.domain ? <Text variant="code">{r.domain}</Text> : <Text variant="body-sm" tone="mute">{t('install.applications.internal')}</Text>) },
                  { key: 'resources', label: t('install.review.resourcesLabel'), grow: true, width: 220, render: (r: BatchRow) => (r.analysis.resources ? t('install.review.resourcesOf', { memory: Math.round(r.analysis.resources.memoryBytes / 1024 ** 2), cpus: r.analysis.resources.cpus }) : '—') },
                ]}
              />
              <Text variant="body-sm" tone="mute">{t('install.review.orderHelp')}</Text>
              {/* § 6.1 step 3: the certificate needs the domain to point to the server (a warning: it may be created meanwhile). */}
              {/* § 10: a technical domain (kanbios.dev) is pointed at the server by the platform when the installation starts. */}
              {exposedRows.map(r => r.dns && !r.dns.pointsToServer && (r.dns.managed
                ? <Alert key={r.deployPath} tone="info" title={t('install.review.dnsManagedTitle', { domain: r.domain })}>{t('install.review.dnsManagedBody', { ip: r.dns.serverIp ?? '—' })}</Alert>
                : (
                  <Alert key={r.deployPath} tone="warn" title={t('install.review.dnsTitle', { domain: r.domain })}>
                    {r.dns.addresses.length
                      ? t('install.review.dnsElsewhere', { addresses: r.dns.addresses.join(', '), ip: r.dns.serverIp ?? '—' })
                      : t('install.review.dnsNone', { ip: r.dns.serverIp ?? '—' })}
                  </Alert>
                )))}
              {/* § 6.1 step 4: the rollback rule that will apply (§ 11.2) and who will reach the environments (§ 12.10). */}
              <DefinitionList
                items={[
                  { key: 'rollback', label: t('install.review.rollbackLabel'), value: t(chosen.environmentType === 'production' ? 'install.review.rollbackProduction' : 'install.review.rollbackOther') },
                  { key: 'access', label: t('install.review.accessLabel'), value: t(chosen.creates[1] ? 'install.review.accessNew' : 'install.review.accessExisting') },
                ]}
              />
              <Text variant="body-sm" tone="mute">{t('install.review.what')}</Text>
            </Stack>
          )}
        </Stack>
      </SplitLayout>
      {stepUpDialog}
      {variablesOf && chosen.environmentType && (
        <BatchVariablesDrawer
          row={rows.find(r => r.deployPath === variablesOf) ?? null}
          target={{ repository: chosen.repository ?? '', ref, environmentType: chosen.environmentType }}
          values={draft.apps[variablesOf]?.values ?? {}}
          onValues={values => setDraft({ ...draft, apps: { ...draft.apps, [variablesOf]: { ...draft.apps[variablesOf], values } } })}
          typedSecrets={typedSecrets[variablesOf] ?? {}}
          onSecretsSaved={saved => { setTypedSecrets({ ...typedSecrets, [variablesOf]: { ...typedSecrets[variablesOf], ...saved } }); void preview.refetch(); }}
          onStepUp={() => setStepUp(true)}
          onClose={() => setVariablesOf(null)}
        />
      )}
      <Dialog
        open={leaving}
        onClose={() => setLeaving(false)}
        title={t('install.leave.title')}
        description={t('install.leave.body')}
        confirmLabel={t('install.leave.confirm')}
        tone="danger"
        onConfirm={leave}
      />
    </>
  );
}
